/* =====================================================================
 * Media Block Styled Components
 * — 미디어 플레이스홀더, 비디오/오디오 플레이어, 파일 카드, 북마크 카드
 * ===================================================================== */

import styled, { css } from 'styled-components';

/* ── 공통 NodeView 래퍼 ── */
const nodeSelectedOutline = css`
  &.ProseMirror-selectednode > div,
  &[data-selected="true"] > div {
    outline: 2px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(251,191,36,0.55)' : 'rgba(245,158,11,0.45)'};
    border-radius: 8px;
  }
`;

/* ================================================================== */
/* MediaPlaceholder                                                    */
/* ================================================================== */

export const PlaceholderWrap = styled.div`
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 0;
  margin: 8px 0;
  overflow: hidden;
  user-select: none;
`;

export const PlaceholderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8125rem;
  font-weight: 500;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    opacity: 0.7;
  }
`;

export const PlaceholderTabBar = styled.div`
  display: flex;
  gap: 0;
  padding: 12px 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

export const PlaceholderTab = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 8px 16px;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.textPrimary : theme.colors.textMuted};
  border-bottom: 2px solid ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark' ? '#fbbf24' : '#f59e0b'
      : 'transparent'};
  transition: color 150ms, border-color 150ms;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

export const PlaceholderBody = styled.div`
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

export const UploadButton = styled.button`
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(245,158,11,0.15)'
      : 'rgba(245,158,11,0.08)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#fcd34d' : '#f59e0b'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(245,158,11,0.25)'
      : 'rgba(245,158,11,0.2)'};
  border-radius: 6px;
  padding: 8px 20px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 150ms, border-color 150ms;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(245,158,11,0.25)'
        : 'rgba(245,158,11,0.14)'};
  }
`;

export const SizeHint = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const EmbedRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

export const EmbedInput = styled.input`
  flex: 1;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
  transition: border-color 150ms;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:focus {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? '#fbbf24' : '#f59e0b'};
  }
`;

export const EmbedButton = styled.button`
  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#fbbf24' : '#f59e0b'};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 150ms;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/* ================================================================== */
/* Resizable Media Container                                           */
/* ================================================================== */

export const ResizableContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  margin: 8px 0;

  &:hover .resize-handle {
    opacity: 1;
  }
`;

export const ResizableInner = styled.div`
  position: relative;
  max-width: 100%;
  min-width: 60px;

  img, video, iframe {
    display: block;
    width: 100%;
    border-radius: 8px;
  }

  video {
    max-height: 480px;
    outline: none;
  }

  iframe {
    aspect-ratio: 16 / 9;
    border: none;
  }
`;

export const ResizeHandle = styled.div<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${({ $side }) => ($side === 'left' ? 'left: -2px;' : 'right: -2px;')}
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: col-resize;
  opacity: 0;
  transition: opacity 150ms;
  z-index: 2;
  user-select: none;

  &::after {
    content: '';
    width: 4px;
    height: 36px;
    max-height: 50%;
    border-radius: 4px;
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.45)'
        : 'rgba(0, 0, 0, 0.35)'};
    transition: background 150ms, height 150ms;
  }

  &:hover::after {
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(251,191,36,0.8)'
        : 'rgba(245,158,11,0.7)'};
    height: 48px;
  }

  &:active::after {
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(251,191,36,1)'
        : 'rgba(245,158,11,0.9)'};
  }
`;

export const ResizeWidthLabel = styled.div`
  position: absolute;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.7)'};
  color: #fff;
  font-size: 0.6875rem;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 3;
`;

/* ================================================================== */
/* Video                                                               */
/* ================================================================== */

export const VideoWrap = styled.div`
  margin: 8px 0;
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgSecondary};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  ${nodeSelectedOutline}

  video {
    display: block;
    width: 100%;
    max-height: 480px;
    border-radius: 8px;
    outline: none;
  }

  iframe {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    border: none;
    border-radius: 8px;
  }
`;

/* ================================================================== */
/* Audio                                                               */
/* ================================================================== */

export const AudioWrap = styled.div`
  margin: 8px 0;
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  ${nodeSelectedOutline}
`;

export const AudioIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(245,158,11,0.15)'
      : 'rgba(245,158,11,0.08)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#fcd34d' : '#f59e0b'};
`;

export const AudioBody = styled.div`
  flex: 1;
  min-width: 0;

  audio {
    width: 100%;
    height: 36px;
    outline: none;
  }
`;

export const AudioFileName = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ================================================================== */
/* File                                                                */
/* ================================================================== */

export const FileCardWrap = styled.div`
  margin: 8px 0;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: default;
  transition: border-color 150ms;
  ${nodeSelectedOutline}

  &:hover {
    border-color: ${({ theme }) => theme.colors.textMuted};
  }
`;

export const FileIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(59, 130, 246, 0.12)'
      : 'rgba(59, 130, 246, 0.08)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#93c5fd' : '#3b82f6'};
`;

export const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FileName = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FileMeta = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 2px;
`;

export const FileDownloadBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: background 150ms, color 150ms;
  display: flex;
  align-items: center;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ================================================================== */
/* Bookmark — 노션 스타일 (왼쪽 텍스트 + 오른쪽 OG 이미지)              */
/* ================================================================== */

export const BookmarkCardWrap = styled.a`
  display: flex;
  align-items: stretch;
  margin: 8px 0;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  text-decoration: none;
  cursor: pointer;
  overflow: hidden;
  height: 108px;
  min-height: 108px;
  transition: border-color 150ms, box-shadow 150ms;
  ${nodeSelectedOutline}

  &:hover {
    border-color: ${({ theme }) => theme.colors.textMuted};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

export const BookmarkBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px 14px;
  gap: 4px;
`;

export const BookmarkTextArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const BookmarkTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const BookmarkDescription = styled.div`
  font-size: 0.8125rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const BookmarkMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: auto;
`;

export const BookmarkFavicon = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 2px;
  flex-shrink: 0;
  object-fit: contain;
`;

export const BookmarkUrl = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const BookmarkImage = styled.div`
  width: 180px;
  height: 108px;
  flex-shrink: 0;
  overflow: hidden;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'};
  border-left: 1px solid ${({ theme }) => theme.colors.border};

  img {
    display: block;
    width: 100% !important;
    height: 100% !important;
    margin: 0 !important;
    border-radius: 0 !important;
    object-fit: cover !important;
    object-position: center center !important;
  }
`;

/* ================================================================== */
/* Image (커스텀 NodeView 래퍼)                                        */
/* ================================================================== */

export const ImageWrap = styled.div`
  margin: 8px 0;
  border-radius: 8px;
  overflow: hidden;
  ${nodeSelectedOutline}

  img {
    display: block;
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    cursor: pointer;
  }
`;

/* ================================================================== */
/* MediaBlockHandle — 미디어 블록 호버 시 왼쪽에 나타나는 + / grip      */
/* ================================================================== */

export const MediaBlockOuter = styled.div`
  position: relative;

  /* 왼쪽 마진까지 hover 감지 영역 확장 (핸들 위치 포함) */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -60px;
    width: 60px;
  }

  &:hover > .media-block-handle {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const MediaBlockHandleWrap = styled.div`
  position: absolute;
  left: -52px;
  top: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s;
  z-index: 10;
`;

export const MediaHandleBtn = styled.div`
  width: 20px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.45;
  background: rgba(0, 0, 0, 0.04);
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: opacity 0.15s, background 0.15s;

  &:hover {
    opacity: 0.85;
    background: rgba(0, 0, 0, 0.08);
  }
`;

/* ================================================================== */
/* 공통 로딩 스피너                                                     */
/* ================================================================== */

export const MediaSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  gap: 8px;

  &::before {
    content: '';
    width: 16px;
    height: 16px;
    border: 2px solid ${({ theme }) => theme.colors.border};
    border-top-color: ${({ theme }) =>
      theme.mode === 'dark' ? '#fbbf24' : '#f59e0b'};
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
